import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  TableIconButton,
  TableIconLink,
} from '../../components/TableIconAction';
import {
  dataTableFootClass,
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import { SortableTh, sortItems, SortState } from '../../components/SortableTh';
import {
  BanIcon,
  Button,
  Callout,
  DateInput,
  EyeIcon,
  ListEmptyState,
  ListPagination,
  LoadingSpinner,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  Select,
  Switch,
  TextInput,
  XIcon,
  useConfirm,
} from '../../components/ui';
import { ExportIconButtons } from '../../components/ExportIconButtons';
import { useAuth } from '../../hooks/useAuth';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CustomerLookupItem } from '../../types/customer';
import { PaymentListItem } from '../../types/payment';
import { PaymentPaidInvoicesButton } from './PaymentPaidInvoicesModal';
import { formatDate, formatMoney } from '../../utils/format';
import {
  collectPages,
  exportTabularList,
  moneyCell,
  sumMoney,
  textCell,
  type ListExportFormat,
} from '../../utils/exportTabularList';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];
const SERVER_SORT_KEYS = new Set(['paymentDate', 'customer']);

const paymentSortGetters: Record<
  string,
  (item: PaymentListItem) => string | number | boolean | null | undefined
> = {
  paymentDate: (p) => p.paymentDate,
  customer: (p) => p.customer.name,
  invoiceNo: (p) => p.invoice?.invoiceNo,
  totalsInvoice: (p) => p.totals.invoice,
  totalsCash: (p) => p.totals.cash,
  totalsTotal: (p) => p.totals.total,
};

export function PaymentListPage() {
  const labels = t().payments;
  const { isAdmin } = useAuth();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<PaymentListItem[]>([]);
  const [customers, setCustomers] = useState<CustomerLookupItem[]>([]);
  const [meta, setMeta] = useState({
    total: 0,
    page: 1,
    pageSize: 20,
    pageCount: 1,
    totals: { invoice: '0.00', cash: '0.00', total: '0.00' },
  });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState(
    searchParams.get('search') ?? '',
  );

  const search = searchParams.get('search') ?? '';
  const customerId = searchParams.get('customerId') ?? '';
  const dateFrom = searchParams.get('dateFrom') ?? '';
  const dateTo = searchParams.get('dateTo') ?? '';
  const unallocatedOnly = searchParams.get('unallocatedOnly') === 'true';
  const page = Number(searchParams.get('page') ?? '1');
  const pageSize =
    Number(searchParams.get('pageSize') ?? '20') || 20;
  const sortBy = searchParams.get('sortBy') ?? 'paymentDate';
  const sortDir =
    searchParams.get('sortDir') === 'asc' ? 'asc' : 'desc';
  const sort: SortState = { key: sortBy, direction: sortDir };

  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  useEffect(() => {
    void api
      .getCustomerLookup({ active: 'true' })
      .then(setCustomers);
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const serverSortBy = SERVER_SORT_KEYS.has(sortBy)
          ? sortBy
          : 'paymentDate';
        const result = await api.getPayments({
          search: search || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          unallocatedOnly: unallocatedOnly || undefined,
          page,
          pageSize,
          sortBy: serverSortBy,
          sortDir: SERVER_SORT_KEYS.has(sortBy) ? sortDir : 'desc',
        });
        if (!cancelled) {
          setItems(result.data);
          setMeta(result.meta);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [
    search,
    customerId,
    dateFrom,
    dateTo,
    unallocatedOnly,
    page,
    pageSize,
    sortBy,
    sortDir,
  ]);

  function updateParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  }

  function toggleSort(key: string) {
    const next = new URLSearchParams(searchParams);
    if (sort.key === key) {
      next.set('sortDir', sort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sortBy', key);
      next.set('sortDir', 'asc');
    }
    next.delete('page');
    setSearchParams(next);
  }

  const displayItems = useMemo(() => {
    if (SERVER_SORT_KEYS.has(sort.key)) return items;
    return sortItems(items, sort, paymentSortGetters);
  }, [items, sort]);

  const hasFilters = Boolean(
    search || customerId || dateFrom || dateTo || unallocatedOnly,
  );

  function clearFilters() {
    setSearchInput('');
    setSearchParams(new URLSearchParams());
  }

  function paymentInvoiceLabel(payment: PaymentListItem): string {
    if (payment.isMultiAllocated) {
      const numbers = payment.allocations
        .map((allocation) => allocation.invoiceNo)
        .filter((invoiceNo): invoiceNo is string => Boolean(invoiceNo));
      return numbers.length
        ? `${labels.multiAllocated} (${numbers.join(', ')})`
        : labels.multiAllocated;
    }
    return (
      payment.invoice?.invoiceNo ??
      payment.allocations[0]?.invoiceNo ??
      labels.unallocated
    );
  }

  async function handleExport(format: ListExportFormat) {
    setExporting(true);
    setError(null);
    try {
      const serverSort = SERVER_SORT_KEYS.has(sort.key);
      const rows = await collectPages((nextPage) =>
        api.getPayments({
          search: search || undefined,
          customerId: customerId ? Number(customerId) : undefined,
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          unallocatedOnly: unallocatedOnly || undefined,
          page: nextPage,
          pageSize: 100,
          sortBy: serverSort ? sort.key : 'paymentDate',
          sortDir: serverSort ? sort.direction : 'desc',
        }),
      );
      const exported = serverSort
        ? rows
        : sortItems(rows, sort, paymentSortGetters);
      exportTabularList({
        filename: 'pliromes.xls',
        title: labels.title,
        meta: [
          { label: t().common.search, value: search || '—' },
          {
            label: labels.customer,
            value:
              customers.find((customer) => String(customer.id) === customerId)
                ?.name ?? '—',
          },
          { label: t().reports.dateFrom, value: dateFrom || '—' },
          { label: t().reports.dateTo, value: dateTo || '—' },
          {
            label: labels.unallocatedOnly,
            value: unallocatedOnly ? t().common.yes : t().common.no,
          },
        ],
        headers: [
          labels.paymentDate,
          labels.customer,
          t().invoices.invoiceNo,
          t().financial.invoice,
          t().financial.cash,
          t().financial.grandTotal,
        ],
        columnWidths: [16, 28, 28, 14, 14, 14],
        numericIndexes: [3, 4, 5],
        rows: exported.map((payment) => [
          textCell(formatDate(payment.paymentDate)),
          textCell(payment.customer.name),
          textCell(paymentInvoiceLabel(payment)),
          moneyCell(payment.totals.invoice),
          moneyCell(payment.totals.cash),
          moneyCell(payment.totals.total),
        ]),
        pdfRows: exported.map((payment) => [
          formatDate(payment.paymentDate),
          payment.customer.name,
          paymentInvoiceLabel(payment),
          formatMoney(payment.totals.invoice),
          formatMoney(payment.totals.cash),
          formatMoney(payment.totals.total),
        ]),
        moneyTotals: [
          sumMoney(exported.map((payment) => payment.totals.invoice)),
          sumMoney(exported.map((payment) => payment.totals.cash)),
          sumMoney(exported.map((payment) => payment.totals.total)),
        ],
        format,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().common.error);
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete(id: number) {
    const ok = await confirm({
      title: labels.softDelete,
      message: labels.softDeleteConfirm,
      confirmText: labels.softDelete,
      cancelText: t().common.cancel,
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.deletePayment(id);
      const removed = items.find((item) => item.id === id);
      setItems((prev) => prev.filter((item) => item.id !== id));
      if (removed) {
        setMeta((prev) => ({
          ...prev,
          total: Math.max(0, prev.total - 1),
          totals: {
            invoice: sumMoney([
              prev.totals.invoice,
              `-${removed.totals.invoice}`,
            ]),
            cash: sumMoney([prev.totals.cash, `-${removed.totals.cash}`]),
            total: sumMoney([prev.totals.total, `-${removed.totals.total}`]),
          },
        }));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t().toasts.saveError);
    }
  }

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <>
            <ExportIconButtons
              disabled={loading || exporting || meta.total === 0}
              onExcel={() => void handleExport('excel')}
              onPdf={() => void handleExport('pdf')}
            />
            {hasFilters ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearFilters}
              >
                <XIcon className="h-3.5 w-3.5" />
                {t().common.clearFilters}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => updateParam('search', searchInput)}
            >
              <SearchIcon className="h-3.5 w-3.5" />
              {t().common.search}
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={() => navigate('/payments/new')}
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {labels.new}
            </Button>
          </>
        }
      >
        <FilterField wide label={t().common.search}>
          <TextInput
            type="search"
            value={searchInput}
            showClearButton={Boolean(searchInput)}
            size="sm"
            placeholder={labels.searchPlaceholder}
            prefix={<SearchIcon className="h-4 w-4" />}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                updateParam('search', searchInput);
              }
            }}
          />
        </FilterField>
        <FilterField label={labels.customer}>
          <Select
            value={customerId}
            size="sm"
            showClearButton={Boolean(customerId)}
            placeholder={labels.customer}
            options={customers.map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            onChange={(e) => updateParam('customerId', e.target.value)}
          />
        </FilterField>
        <FilterField label={t().reports.dateFrom}>
          <DateInput
            value={dateFrom}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={t().reports.dateFrom}
            onChange={(e) => updateParam('dateFrom', e.target.value)}
          />
        </FilterField>
        <FilterField label={t().reports.dateTo}>
          <DateInput
            value={dateTo}
            showClearButton
            showQuickFill={false}
            showFormatHint={false}
            placeholder={t().reports.dateTo}
            onChange={(e) => updateParam('dateTo', e.target.value)}
          />
        </FilterField>
        <FilterField label={labels.unallocatedOnly}>
          <div className="flex h-8 items-center rounded-lg border border-line bg-surface-card px-3">
            <Switch
              checked={unallocatedOnly}
              onChange={(e) =>
                updateParam(
                  'unallocatedOnly',
                  e.target.checked ? 'true' : '',
                )
              }
              aria-label={labels.unallocatedOnly}
              size="sm"
            />
          </div>
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel
        footer={
          !loading && meta.total > 0 ? (
            <ListPagination
              page={meta.page}
              totalPages={meta.pageCount}
              total={meta.total}
              itemsPerPage={meta.pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPageChange={(nextPage) =>
                updateParam('page', String(nextPage))
              }
              onItemsPerPageChange={(size) =>
                updateParam('pageSize', String(size))
              }
            />
          ) : null
        }
      >
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : displayItems.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="whitespace-nowrap px-3 py-2 font-medium">
                  {t().common.actions}
                </th>
                <SortableTh
                  sortKey="paymentDate"
                  sort={sort}
                  onSort={toggleSort}
                >
                  {labels.paymentDate}
                </SortableTh>
                <SortableTh sortKey="customer" sort={sort} onSort={toggleSort}>
                  {labels.customer}
                </SortableTh>
                <SortableTh sortKey="invoiceNo" sort={sort} onSort={toggleSort}>
                  {t().invoices.invoiceNo}
                </SortableTh>
                <SortableTh
                  sortKey="totalsInvoice"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.invoice}
                </SortableTh>
                <SortableTh
                  sortKey="totalsCash"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {t().financial.cash}
                </SortableTh>
                <SortableTh
                  sortKey="totalsTotal"
                  sort={sort}
                  onSort={toggleSort}
                  align="right"
                >
                  {labels.paymentTotal}
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {displayItems.map((payment) => (
                <tr key={payment.id} className="border-t border-line">
                  <td className="whitespace-nowrap px-3 py-2">
                    <div className="inline-flex items-center gap-0.5">
                      <TableIconLink
                        to={`/payments/${payment.id}`}
                        label={labels.view}
                      >
                        <EyeIcon className="h-4 w-4" />
                      </TableIconLink>
                      <TableIconLink
                        to={`/payments/${payment.id}/edit`}
                        label={labels.editAction}
                      >
                        <PencilIcon className="h-4 w-4" />
                      </TableIconLink>
                      {isAdmin ? (
                        <TableIconButton
                          label={labels.softDelete}
                          variant="danger"
                          onClick={() => void handleDelete(payment.id)}
                        >
                          <BanIcon className="h-4 w-4" />
                        </TableIconButton>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-3 py-2">
                    {formatDate(payment.paymentDate)}
                  </td>
                  <td className="px-3 py-2">{payment.customer.name}</td>
                  <td className="px-3 py-2">
                    {payment.isMultiAllocated ? (
                      <span className="text-ink">
                        {labels.multiAllocated}
                        <span className="mt-0.5 block text-xs text-ink-muted">
                          {payment.allocations
                            .map((a) => a.invoiceNo)
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </span>
                    ) : payment.invoice ? (
                      <Link
                        to={`/invoices/${payment.invoice.id}`}
                        className="text-brand hover:underline"
                      >
                        {payment.invoice.invoiceNo}
                      </Link>
                    ) : payment.allocations[0] ? (
                      <Link
                        to={`/invoices/${payment.allocations[0].invoiceId}`}
                        className="text-brand hover:underline"
                      >
                        {payment.allocations[0].invoiceNo}
                      </Link>
                    ) : (
                      <span className="text-ink-muted">
                        {labels.unallocated}
                      </span>
                    )}
                    <span className="ml-1 inline-flex align-middle">
                      <PaymentPaidInvoicesButton
                        allocations={payment.allocations}
                      />
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(payment.totals.invoice)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMoney(payment.totals.cash)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-medium">
                    {formatMoney(payment.totals.total)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className={dataTableFootClass}>
              <tr>
                <td className="px-3 py-2" colSpan={4}>
                  {`${t().common.total} · ${(meta.total === 1
                    ? labels.listPaymentCountOne
                    : labels.listPaymentCount
                  ).replace('{count}', String(meta.total))}`}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.invoice)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.cash)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(meta.totals.total)}
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
